// accountingUtils.js - Helper functions for accounting operations

import { apiClient } from "../apiClient";

// ==========================================
// CURRENCY CONVERSION
// ==========================================

/**
 * Convert PLN to groszy (backend storage format)
 * @param {number|string} pln - Amount in PLN
 * @returns {number} Amount in groszy
 */
export const plnToGroszy = (pln) => {
  return Math.round(parseFloat(pln) * 100);
};

/**
 * Convert groszy to PLN (user display format)
 * @param {number} groszy - Amount in groszy
 * @returns {string} Formatted PLN amount
 */
export const groszyToPln = (groszy) => {
  return (groszy / 100).toFixed(2);
};

// ==========================================
// DATE FORMATTING
// ==========================================

/**
 * Format ISO date to human-readable format
 * @param {string} isoDate - ISO date string (e.g., "2026-01-20")
 * @returns {string} Formatted date (e.g., "20 Jan 2026")
 */
export const formatDate = (isoDate) => {
  if (!isoDate || isoDate === "unknown") return "Unknown";
  const d = new Date(isoDate);
  return d.toLocaleDateString('en-GB', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
};

/**
 * Get today's date in ISO format
 * @returns {string} ISO date string
 */
export const getTodayISO = () => {
  return new Date().toISOString().split("T")[0];
};

/**
 * Get first day of current month in ISO format
 * @returns {string} ISO date string
 */
export const getFirstDayOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
};

/**
 * Get month name from ISO date
 * @param {string} isoDate - ISO date string
 * @returns {string} Month name (e.g., "January 2026")
 */
export const getMonthName = (isoDate) => {
  const d = new Date(isoDate);
  return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

// ==========================================
// CATEGORY MANAGEMENT
// ==========================================

/**
 * Find existing category or create new one
 * @param {string} type - "income" or "expense"
 * @param {string} name - Category name
 * @param {string} token - Auth token (no longer needed with apiClient)
 * @returns {Promise<number>} Category ID
 */
export const findOrCreateCategory = async (type, name, token) => {
  // ⚠️ token argument kept for compatibility but ignored (apiClient handles auth)
  
  try {
    // 1. Fetch existing categories
    const categories = await apiClient.get("/accounting/categories");
    
    // 2. Search for existing category (case-insensitive, trimmed)
    const normalizedName = name.trim().toLowerCase();
    const existing = categories.find(
      c => c.type === type && c.name.trim().toLowerCase() === normalizedName
    );
    
    if (existing) return existing.id;
    
    // 3. Create new category
    const newCategory = await apiClient.post("/accounting/categories", {
      type,
      name: name.trim(),
      is_active: 1
    });
    
    return newCategory.id;
    
  } catch (err) {
    throw err;
  }
};

/**
 * Fetch all categories
 * @param {string} token - Auth token (no longer needed with apiClient)
 * @returns {Promise<Array>} Categories array
 */
export const fetchCategories = async (token) => {
  // ⚠️ token argument kept for compatibility but ignored (apiClient handles auth)
  
  return await apiClient.get("/accounting/categories");
};

/**
 * Get category name by ID
 * @param {number} categoryId - Category ID
 * @param {Array} categories - Categories array
 * @returns {string} Category name
 */
export const getCategoryName = (categoryId, categories) => {
  const category = categories.find(c => c.id === categoryId);
  return category ? category.name : "Unknown";
};

// ==========================================
// PREDEFINED CATEGORIES
// ==========================================

export const PREDEFINED_EXPENSE_CATEGORIES = [
  // Ingredients
  "Flour",
  "Yeast",
  "Oil",
  "Milk",
  "Salt",
  "Sugar",
  "Eggs",
  
  // Utilities
  "Electricity",
  "Water",
  "Gas",
  "Internet/Phone",
  
  // Operations
  "Rent",
  "Fuel (Vehicle)",
  "Vehicle Maintenance",
  "Equipment Repairs",
  "Packaging Materials",
  "Transportation",
  
  // HR
  "Salaries",
  "Partner Advances",
  
  // Other
  "Miscellaneous"
];

// ==========================================
// EXPORT FUNCTIONS
// ==========================================

/**
 * Export data to PDF using jsPDF
 * @param {string} title - PDF title
 * @param {Array} headers - Table headers
 * @param {Array} rows - Table rows
 * @param {string} filename - Output filename
 */
export const exportToPDF = async (title, headers, rows, filename) => {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  
  const doc = new jsPDF();
  
  doc.setFontSize(16);
  doc.text(title, 14, 20);
  
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 30,
    theme: 'grid',
    styles: { fontSize: 10 },
    headStyles: { fillColor: [45, 47, 94] }
  });
  
  doc.save(filename);
};

/**
 * Export data to Excel using xlsx library
 * @param {Array} data - Data array
 * @param {string} filename - Output filename (without extension)
 */
export const exportToExcel = (data, filename) => {
  if (!data || data.length === 0) {
    alert("No data to export");
    return;
  }

  try {
    import('xlsx').then(XLSX => {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      XLSX.writeFile(workbook, `${filename}.xlsx`);
    }).catch(err => {
      alert("Excel export requires 'xlsx' package. Downloading CSV instead...");
      exportToCSV(data, `${filename}.csv`);
    });
  } catch (err) {
    alert("Excel export failed. Downloading CSV instead...");
    exportToCSV(data, `${filename}.csv`);
  }
};

/**
 * Export data to CSV
 * @param {Array} data - Data array
 * @param {string} filename - Output filename
 */
export const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    alert("No data to export");
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(","),
    ...data.map(row => headers.map(h => row[h] || "").join(","))
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
};

// ==========================================
// DELIVERY LINKING HELPERS
// ==========================================

/**
 * Parse delivery link from notes field
 * @param {string} notes - Notes field
 * @returns {number|null} Delivery item ID or null
 */
export const parseDeliveryLink = (notes) => {
  if (!notes) return null;
  const match = notes.match(/DELIVERY_LINK:(\d+)/);
  return match ? parseInt(match[1]) : null;
};

/**
 * Create delivery link string for notes field
 * @param {number} deliveryItemId - Delivery item ID
 * @param {string} existingNotes - Existing notes
 * @returns {string} Formatted notes with delivery link
 */
export const createDeliveryLink = (deliveryItemId, existingNotes = "") => {
  const link = `DELIVERY_LINK:${deliveryItemId}`;
  return existingNotes ? `${link}|${existingNotes}` : link;
};

/**
 * Remove delivery link from notes
 * @param {string} notes - Notes with delivery link
 * @returns {string} Clean notes without delivery link
 */
export const removeDeliveryLink = (notes) => {
  if (!notes) return "";
  return notes.replace(/DELIVERY_LINK:\d+\|?/, "").trim();
};